import { type Dispatch, type SetStateAction } from 'react';

import { Cancel, Search } from '@mui/icons-material';
import { DatePicker } from '@mui/lab';
import {
    Autocomplete,
    IconButton,
    InputAdornment,
    MenuItem,
    TextField,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import Input from '../../../../../components/forms/Input';
import Select from '../../../../../components/forms/Select';
import { StyledLabel } from '../../../../../components/forms/styles';
import ClassesContainer from '../ClassesContainer';
import { type Option, TimeFilter, DateFilterType } from '../types';

interface ClassFiltersInterface {
    setPage: Dispatch<SetStateAction<number>>;
}

const Container = styled.div`
    display: grid;
    grid-template-columns: 220px 160px 200px 200px 180px 180px 50px;
    gap: 10px;
    margin-bottom: 8px;

    /* Improve focus visibility */
    * {
        &:focus-visible {
            outline: 2px solid ${(props) => props.theme.colors.primary.main};
            outline-offset: 2px;
        }
    }
`;

const RadioColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 8px;

    .MuiToggleButtonGroup-root {
        height: 32px;
        margin-top: 4px;
        width: 100%;

        .MuiToggleButton-root {
            padding: 4px 8px;
            font-size: 12px;
            text-transform: none;
            border-color: ${(props) => props.theme.colors.border};
            color: ${(props) => props.theme.colors.text.secondary};
            position: relative;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
            min-width: 0;

            &.Mui-selected {
                background-color: ${(props) => props.theme.colors.primary.main};
                color: ${(props) => props.theme.colors.white};

                &:hover {
                    background-color: ${(props) => props.theme.colors.primary.dark};
                }
            }

            &:hover {
                background-color: ${(props) => props.theme.colors.primary.light};
            }

            &:focus-visible {
                outline: 2px solid ${(props) => props.theme.colors.primary.main};
                outline-offset: 2px;
                z-index: 1;
            }
        }
    }
`;

const StyledIconButton = styled(IconButton)`
    margin-top: 24px;
`;

const Count = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    /* Ensure good contrast for count text */
    color: ${(props) => props.theme.colors.text.main};
    font-weight: 500;
`;

const ScreenReaderText = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;

const ClassFilters = ({ setPage }: ClassFiltersInterface) => {
    const { t } = useTranslation('admin_settings');

    const {
        instructors,
        filteredClasses,
        selectedInstructor,
        setSelectedInstructor,
        searchValue,
        setSearchValue,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        timeFilter,
        setTimeFilter,
        useCreatedDateFilter,
        setUseCreatedDateFilter
    } = useContainer(ClassesContainer);

    const clearAllFilters = () => {
        setPage(1);
        setSearchValue('');
        setTimeFilter(TimeFilter.Current);
        setSelectedInstructor(null);
        setStartDate(null);
        setEndDate(null);
    };

    const onSearchChange = (value: string) => {
        setPage(1);
        setSearchValue(value);
    };

    const onStartDateChange = (date: Dayjs | null) => {
        setStartDate(date);
    };

    const onEndDateChange = (date: Dayjs | null) => {
        setEndDate(date);
    };

    const onCreatedAfterDateChange = (date: Dayjs | null) => {
        setPage(1);
        setStartDate(date); // Use startDate for creation date filtering
    };

    const onTimeFilterChange = (event) => {
        setPage(1);
        setTimeFilter(event.target.value);
    };

    const options: Option[] = instructors.map((instructor) => {
        const { user_name: username, email } = instructor;

        return {
            email,
            label: username
        };
    });

    const returnFilterCount = () => {
        const hasFilters = searchValue !== '' || selectedInstructor || startDate || endDate;

        if (hasFilters) {
            const returnText = (count: number): string => {
                switch (count) {
                    case 0:
                        return t('classes.no-classes-found');
                    case 1:
                        return t('classes.showing-1-results');
                    default:
                        return t('classes.showing-results-with-count', { count });
                }
            };

            return <div>{returnText(filteredClasses.length)}</div>;
        }

        return null;
    };

    return (
        <>
            <Container role="search" aria-label={t('classes.filter-classes')} data-testid="class-filters-container">
                <div>
                    <Input
                        fullWidth
                        id="search"
                        size="small"
                        value={searchValue}
                        label={t('classes.search')}
                        onChange={(e) => onSearchChange(e.target.value)}
                        name="search"
                        aria-describedby="search-help"
                        data-testid="search-input"
                        endAdornment={
                            <InputAdornment position="end">
                                <Search aria-hidden="true" />
                            </InputAdornment>
                        }
                    />
                    <ScreenReaderText id="search-help">{t('classes.search-help')}</ScreenReaderText>
                </div>

                <div>
                    <Select
                        name="time-filter"
                        fullWidth
                        value={timeFilter}
                        label={t('classes.status')}
                        onChange={onTimeFilterChange}
                        aria-describedby="status-help"
                        data-testid="status-filter-select"
                    >
                        <MenuItem value={TimeFilter.All} data-testid="status-filter-all">
                            {t('classes.show-all')}
                        </MenuItem>
                        <MenuItem value={TimeFilter.Current} data-testid="status-filter-current">
                            {t('classes.show-current')}
                        </MenuItem>
                        <MenuItem value={TimeFilter.Completed} data-testid="status-filter-completed">
                            {t('classes.show-past')}
                        </MenuItem>
                        <MenuItem value={TimeFilter.Archived} data-testid="status-filter-archived">
                            {t('classes.show-archived')}
                        </MenuItem>
                        <MenuItem value={TimeFilter.Draft} data-testid="status-filter-draft">
                            {t('classes.show-draft')}
                        </MenuItem>
                    </Select>
                    <ScreenReaderText id="status-help">{t('classes.status-help')}</ScreenReaderText>
                </div>

                <div>
                    <StyledLabel htmlFor="instructor">{t('classes.instructor')}</StyledLabel>
                    <Autocomplete
                        value={selectedInstructor}
                        onChange={(_, newValue) => {
                            setPage(1);
                            setSelectedInstructor(newValue);
                        }}
                        isOptionEqualToValue={(option, val) => option.email === val.email}
                        id="instructor"
                        options={options}
                        size="small"
                        aria-describedby="instructor-help"
                        data-testid="instructor-filter-autocomplete"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={t('classes.all-instructor')}
                                aria-label={t('classes.instructor')}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.email}>
                                {option.label}
                                <ScreenReaderText> - {option.email}</ScreenReaderText>
                            </li>
                        )}
                    />
                    <ScreenReaderText id="instructor-help">{t('classes.instructor-help')}</ScreenReaderText>
                </div>

                <RadioColumn>
                    <StyledLabel as="legend">{t('classes.date-filter-type')}</StyledLabel>
                    <ToggleButtonGroup
                        value={useCreatedDateFilter ? DateFilterType.Created : DateFilterType.Range}
                        exclusive
                        onChange={(_, newValue) => {
                            if (newValue !== null) {
                                setUseCreatedDateFilter(newValue === DateFilterType.Created);
                            }
                        }}
                        size="small"
                        aria-label={t('classes.date-filter-type')}
                        role="radiogroup"
                        aria-describedby="date-filter-help"
                        data-testid="date-filter-type-toggle"
                    >
                        <ToggleButton
                            value={DateFilterType.Range}
                            aria-label={t('classes.class-dates')}
                            role="radio"
                            aria-checked={!useCreatedDateFilter}
                        >
                            {t('classes.class-dates')}
                        </ToggleButton>
                        <ToggleButton
                            value={DateFilterType.Created}
                            aria-label={t('classes.created')}
                            role="radio"
                            aria-checked={useCreatedDateFilter}
                        >
                            {t('classes.created')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <ScreenReaderText id="date-filter-help">{t('classes.date-filter-help')}</ScreenReaderText>
                </RadioColumn>

                <div>
                    <StyledLabel htmlFor="from">{t('classes.from')}</StyledLabel>
                    <DatePicker
                        value={startDate}
                        onChange={(date: Dayjs | null) => onStartDateChange(date)}
                        renderInput={(params) => (
                            <TextField
                                size="small"
                                fullWidth
                                id="from"
                                name="from"
                                aria-describedby="from-help"
                                data-testid="from-date-picker"
                                {...params}
                            />
                        )}
                    />
                    <ScreenReaderText id="from-help">{t('classes.from-help')}</ScreenReaderText>
                </div>

                <div>
                    <StyledLabel htmlFor="to">{t('classes.to')}</StyledLabel>
                    <DatePicker
                        value={endDate}
                        onChange={(date: Dayjs | null) => onEndDateChange(date)}
                        renderInput={(params) => (
                            <TextField
                                size="small"
                                fullWidth
                                id="to"
                                name="to"
                                aria-describedby="to-help"
                                data-testid="to-date-picker"
                                {...params}
                            />
                        )}
                    />
                    <ScreenReaderText id="to-help">{t('classes.to-help')}</ScreenReaderText>
                </div>

                <div>
                    <Tooltip arrow title={t('classes.clear-selection')}>
                        <StyledIconButton
                            onClick={clearAllFilters}
                            aria-label={t('classes.clear-all-filters')}
                            data-testid="clear-filters-button"
                        >
                            <Cancel aria-hidden="true" />
                        </StyledIconButton>
                    </Tooltip>
                </div>
            </Container>
            <Count
                role="status"
                aria-live="polite"
                aria-label={t('classes.filter-results')}
                data-testid="filter-results-count"
            >
                {returnFilterCount()}
            </Count>
        </>
    );
};

export default ClassFilters;
